<script lang="ts" setup>
import type { ISelectItem, ToastHtmlElement } from "@/types"
import { useHead } from "@unhead/vue"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { ref } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { useCreateAlbum } from "@/api"
import {
  Button,
  Datepicker,
  FileInput,
  Form,
  FormSection,
  GooglePlaceInput,
  Icon,
  Input,
  Select,
  Switch,
  Textarea,
  Toast,
} from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { ForbiddenException, ValidationException } from "@/http"
import { SidebarHostEventLayout } from "@/layouts"
import { AlbumSettings, Location } from "@/models"
import { Album } from "@/models/album"
import { locationValidation } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/album/create", value => Number(value))

const { t } = useTranslation()

const router = useRouter()

const errorToast = ref<ToastHtmlElement | null>(null)

const validationErrors = ref<Record<string, any>>({})
function clearValidationErrors(key?: string) {
  if (key) {
    delete validationErrors.value[key]
  }
  else {
    validationErrors.value = {}
  }
}

const validationSchema = toTypedSchema(
  z
    .object({
      title: z
        .string()
        .min(1, t("album_create.validation.title_required"))
        .max(40, t("album_create.validation.title_max")),
      date: z.date({ required_error: t("album_create.validation.date_required") }),
      description: z.optional(z.string().max(1000, t("album_create.validation.description_max"))),
      coverImage: z.optional(z.any().refine(file => file instanceof File, t("album_create.validation.file_type"))),
      privacy: z.enum(["public", "protected", "private"]),
      passcode: z.optional(z.string()),
      location: locationValidation(),
      enableUploads: z.boolean(),
    })
    .refine(
      (fields) => {
        return !(fields.privacy === "protected" && !/^\d{4,10}$/.test(fields.passcode ?? ""))
      },
      {
        message: t("album_create.validation.passcode_required"),
        path: ["passcode"],
      },
    ),
)

const { values, errors, handleSubmit, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    title: "",
    description: "",
    privacy: "public",
    location: undefined,
    enableUploads: true,
  },
})

const { mutate, isPending } = useCreateAlbum()

const submit = handleSubmit((values) => {
  if (!eventVaultId.value) {
    return
  }

  const settings = new AlbumSettings({
    disableUploads: !values.enableUploads,
  })

  const album = new Album({
    title: values.title,
    date: values.date,
    description: values.description,
    coverImage: values.coverImage,
    passcode: values.passcode,
    isLocked: values.privacy === "protected",
    isHidden: values.privacy === "private",
    location: values.location ? new Location(values.location) : undefined,
    settings,
  })

  mutate({ album, eventVaultId: eventVaultId.value }, {
    onSuccess: () => {
      router.push({ name: "/event/[id]/albums", params: { id: eventVaultId.value! } })
    },
    onError: (e: unknown) => {
      if (e instanceof ValidationException) {
        validationErrors.value = e.validationErrors
        return
      }

      if (e instanceof ForbiddenException) {
        const message = e.response?.data.Message ?? t("album_create.forbidden_error")
        errorToast.value?.showToast({ content: message, title: t("something_went_wrong"), icon: "Caution" })
        return
      }

      errorToast.value?.showToast()
    },
  })
})

const privacyOptions: Array<ISelectItem> = [
  { label: t("public"), value: "public" },
  { label: t("protected"), value: "protected" },
  { label: t("private"), value: "private" },
]

useHead({ title: t("album_create.heading") })
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-heading font-bold text-2xl mb-8">
      {{ t("album_create.heading") }}
    </h1>
    <Form @submit="submit">
      <FormSection :title="t('album_create.details_section_title')" :description="t('album_create.details_section_description')" icon="Folder">
        <Input
          :label="t('album_create.name_label')" name="title"
          :error="validationErrors?.Title?.length ? validationErrors.Title[0] : ''"
        />
        <Datepicker
          id="album-event-date" name="date" :label="t('album_create.date_label')"
          :error="validationErrors?.AlbumDate?.length ? validationErrors.AlbumDate[0] : ''"
          @change="clearValidationErrors('AlbumDate')"
        />
        <GooglePlaceInput
          id="location" name="location.formattedAddress"
          :label="t('album_create.location_label')" :error="errors.location"
          @update:location="(loc) => setFieldValue('location', loc)"
        />
        <Textarea
          id="album-description" :label="t('album_create.description_label')"
          name="description"
          :error="validationErrors?.Description?.length ? validationErrors.Description[0] : ''"
        />
      </FormSection>

      <FormSection :title="t('album_create.image_section_title')" :description="t('album_create.image_hint')" icon="Images">
        <FileInput
          id="album-cover" name="coverImage"
          accept="image/jpeg,image/png,image/gif,image/webp"
          :label="t('album_create.cover_label')"
        />
      </FormSection>

      <FormSection :title="t('album_create.privacy_section_title')" icon="FolderLock">
        <template #description>
          <h4 class="font-heading font-bold mb-4">
            {{ t("album_create.privacy_subheading") }}
          </h4>
          <dl>
            <dt class="font-bold mb-1">
              {{ t("public") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.public_description") }}
            </dd>
            <dt class="font-bold mb-1">
              {{ t("private") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.private_description") }}
            </dd>
            <dt class="font-bold mb-1">
              {{ t("protected") }}
            </dt>
            <dd class="mb-2">
              {{ t("album_create.protected_description") }}
            </dd>
          </dl>
        </template>
        <Switch
          variant="icon" name="enableUploads"
          :label="t('album_create.enable_uploads_label')"
        />
        <Select
          :label="t('album_create.privacy_label')" name="privacy"
          :items="privacyOptions"
        />

        <Input
          id="passcode" name="passcode" :label="t('album_create.passcode_label')"
          type="password" :disabled="values.privacy !== 'protected'"
          :hint="t('album_create.passcode_hint')"
        />
      </FormSection>

      <template #actions>
        <div class="flex justify-end">
          <Button variant="primary" :disabled="isPending" @click.prevent="submit">
            <template #suffixIcon>
              <Icon v-if="isPending" icon="Loading" />
            </template>
            {{ t("submit") }}
          </Button>
        </div>
      </template>
    </Form>
  </SidebarHostEventLayout>

  <Toast ref="errorToast" variant="danger" />
</template>
